import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  api,
  NIVEAUX,
  type Document,
  type JeuMessages,
  type Messages,
} from "./api";
import { ApercuTypst } from "./Maths";
import { couleursNiveaux, libellesNiveaux, type NiveauMaitrise } from "./palette";

const DELAI_ENREGISTREMENT = 900;

const LIBELLES_COURTS: Record<string, string> = {
  non_acquis: "Non acquis",
  en_cours: "En cours",
  fragile: "Fragile",
  acquis: "Acquis",
};

type Copie = {
  copie_id: number;
  prenom: string;
  nom: string;
  statut: string;
  note_finale: number;
  competences: { code: string; libelle: string; taux: number; niveau: string }[];
};

export default function Bilans({ dsId }: { dsId: number }) {
  const [competences, setCompetences] = useState<{ code: string; libelle: string }[]>([]);
  const [messages, setMessages] = useState<Messages>({});
  const [copies, setCopies] = useState<Copie[]>([]);
  const [appreciations, setAppreciations] = useState<Record<number, string>>({});
  const [documents, setDocuments] = useState<Document[]>([]);
  const [etat, setEtat] = useState<"repos" | "redaction" | "production">("repos");
  const [message, setMessage] = useState<string | null>(null);
  // Le bandeau du haut de section est hors de vue quand on enregistre les
  // messages — le bouton est douze zones de texte plus bas. Sans accusé de
  // réception à côté de lui, l'enregistrement réussit sans que rien ne
  // change à l'écran : le bouton paraît inerte.
  const [confirmation, setConfirmation] = useState<string | null>(null);
  const [cout, setCout] = useState<number | null>(null);

  const enAttente = useRef<Map<number, string>>(new Map());
  const minuteur = useRef<number | undefined>(undefined);

  const recharger = useCallback(() => {
    api.lireDs(dsId).then((s) => setCompetences(s.competences));
    api.lireMessages(dsId).then(setMessages);
    api.lireResultats(dsId).then((r) => setCopies(r.copies.filter((c) => c.statut === "presente")));
    api.lireAppreciations(dsId).then(setAppreciations);
    api.lireDocuments(dsId).then(setDocuments);
  }, [dsId]);

  useEffect(recharger, [recharger]);

  const complet = useMemo(
    () => competences.length > 0 && competences.every((c) => messages[c.code]),
    [competences, messages],
  );

  const rediger = async () => {
    setEtat("redaction");
    setMessage(null);
    try {
      const proposition = await api.redigerMessages(dsId);
      const jeu: JeuMessages = proposition.jeu;
      await api.enregistrerMessages(dsId, jeu);
      setCout(proposition.cout_dollars);
      const bloquantes = proposition.anomalies.filter((a) => a.gravite === "erreur");
      setMessage(
        bloquantes.length > 0
          ? `Enregistré avec ${bloquantes.length} anomalie(s) : ${bloquantes[0].message}`
          : "Messages rédigés et enregistrés.",
      );
      setMessages(await api.lireMessages(dsId));
    } catch (e) {
      setMessage((e as Error).message);
    } finally {
      setEtat("repos");
    }
  };

  const modifierMessage = (code: string, niveau: string, texte: string) => {
    setConfirmation(null);   // retouché, donc plus enregistré
    setMessages((precedent) => ({
      ...precedent,
      [code]: { ...precedent[code], [niveau]: texte },
    }));
  };

  const enregistrerMessages = async () => {
    const jeu: JeuMessages = {
      messages: competences
        .filter((c) => messages[c.code])
        .map((c) => ({
          code: c.code,
          non_acquis: messages[c.code].non_acquis ?? "",
          en_cours: messages[c.code].en_cours ?? "",
          fragile: messages[c.code].fragile ?? "",
          acquis: messages[c.code].acquis ?? "",
        })),
    };
    try {
      const bilan = await api.enregistrerMessages(dsId, jeu);
      setConfirmation(`${bilan.enregistres} messages enregistrés.`);
    } catch (e) {
      setConfirmation((e as Error).message);
    }
  };

  // Les appréciations s'écrivent au fil de l'eau : écriture différée, comme
  // pour la saisie des notes.
  const ecrireAppreciations = useCallback(async () => {
    const lots = [...enAttente.current.entries()];
    enAttente.current.clear();
    await Promise.all(
      lots.map(([copieId, texte]) => api.enregistrerAppreciation(copieId, texte)),
    );
  }, []);

  const modifierAppreciation = (copieId: number, texte: string) => {
    setAppreciations((precedent) => ({ ...precedent, [copieId]: texte }));
    enAttente.current.set(copieId, texte);
    window.clearTimeout(minuteur.current);
    minuteur.current = window.setTimeout(ecrireAppreciations, DELAI_ENREGISTREMENT);
  };

  const produire = async () => {
    await ecrireAppreciations();
    setEtat("production");
    setMessage(null);
    try {
      const document = await api.produireBilans(dsId);
      setMessage(`${Math.round(document.octets / 1024)} ko produits.`);
      setDocuments(await api.lireDocuments(dsId));
    } catch (e) {
      setMessage((e as Error).message);
    } finally {
      setEtat("repos");
    }
  };

  const dernier = documents.find((d) => d.type === "feedback_individuel");

  return (
    <section className="bilans">
      <header className="saisie-entete">
        <div>
          <h2>Bilans individuels</h2>
          <p className="sous-titre">
            {competences.length} compétences · {copies.length} copies présentes
            {cout !== null && ` · dernier appel ${cout.toFixed(3)} $`}
          </p>
        </div>
        <div className="actions">
          <button type="button" onClick={rediger} disabled={etat !== "repos"}>
            {etat === "redaction"
              ? "Rédaction en cours…"
              : complet
                ? "Réécrire les messages (payant)"
                : "Rédiger les messages (payant)"}
          </button>
          <button
            type="button"
            onClick={produire}
            disabled={etat !== "repos" || copies.length === 0}
          >
            {etat === "production" ? "Composition…" : "Produire les bilans"}
          </button>
          {dernier && (
            <a className="telechargement" href={api.lienTelechargement(dernier.id)}>
              Télécharger le PDF
            </a>
          )}
        </div>
      </header>

      {message && <p className="message">{message}</p>}

      {competences.length === 0 && (
        <p className="aide">
          Aucune compétence enregistrée. Passez d'abord par l'onglet{" "}
          <strong>Compétences</strong> : les messages s'appuient sur elles.
        </p>
      )}

      {competences.length > 0 && (
        <>
          <h3>Messages de remédiation</h3>
          <p className="aide">
            Écrits une fois pour le devoir, ils s'affichent sur la fiche de chaque
            élève selon le niveau qu'il a atteint. Modifiables ici.
          </p>

          {competences.map((competence) => (
            <article key={competence.code} className="fiche-competence">
              <header>
                <strong>{competence.code}</strong>
                <span>{competence.libelle}</span>
              </header>
              {NIVEAUX.map((niveau) => (
                <div key={niveau} className="ligne-message">
                  <span
                    className="pastille-niveau"
                    style={{ background: couleursNiveaux[niveau as NiveauMaitrise] }}
                    title={libellesNiveaux[niveau as NiveauMaitrise]}
                  />
                  <label>{LIBELLES_COURTS[niveau]}</label>
                  <div className="champ-message">
                    <textarea
                      rows={2}
                      value={messages[competence.code]?.[niveau] ?? ""}
                      placeholder="Non rédigé"
                      onChange={(e) =>
                        modifierMessage(competence.code, niveau, e.target.value)
                      }
                    />
                    <ApercuTypst texte={messages[competence.code]?.[niveau] ?? ""} />
                  </div>
                </div>
              ))}
            </article>
          ))}

          <div className="ligne-bouton">
            <button type="button" onClick={enregistrerMessages}>
              Enregistrer les messages
            </button>
            {confirmation && <span className="message">{confirmation}</span>}
          </div>

          <h3>Appréciations</h3>
          <p className="aide">
            Facultatives, et de votre main : elles n'ont pas d'équivalent
            automatique. Enregistrées au fil de la frappe.
          </p>
          <table className="appreciations">
            <tbody>
              {copies.map((copie) => (
                <tr key={copie.copie_id}>
                  <th>
                    {copie.nom} {copie.prenom}
                    <div className="note-eleve">
                      {copie.note_finale.toLocaleString("fr-FR")}
                    </div>
                  </th>
                  <td>
                    <textarea
                      rows={2}
                      value={appreciations[copie.copie_id] ?? ""}
                      placeholder="Appréciation (facultative)"
                      onChange={(e) =>
                        modifierAppreciation(copie.copie_id, e.target.value)
                      }
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </section>
  );
}
